import React, { createContext, useCallback, useEffect, useState } from "react";

import { AgencyNoticeExhibition } from "~/apps/shared/constants/enums";
import { useContextFactory } from "~/apps/shared/hooks/use-context-factory";
import { Error } from "~/apps/shared/types";
import { isProdEnv } from "~/apps/shared/utils/env";

import { agencyNoticesHelper } from "../helpers/agency-notices.helper";
import { usePoll } from "../hooks/use-poll";
import { AgencyNotice } from "../models/agency-notice.model";
import * as agencyNoticesService from "./agency-notices.service";
import { useUser } from "./user.context";

const ONE_MINUTE_IN_MILLISECONDS = 1 * 60 * 1000;
const TEN_MINUTES_IN_MILLISECONDS = 10 * 60 * 1000;

type Actions = {
  closeAgencyNoticeHeaderBanner: () => void;
};

type State = {
  agencyNotices: AgencyNotice[] | null;
  errorLoadingAgencyNotices: Error | null;
  isAgencyNoticeHeaderBannerVisible: boolean;
  isLoadingAgencyNotices: boolean;
};

const initialState: State = {
  agencyNotices: null,
  errorLoadingAgencyNotices: null,
  isAgencyNoticeHeaderBannerVisible: true,
  isLoadingAgencyNotices: false,
};

type Context = Actions & State;

const AgencyNoticesContext = createContext<Context>({
  ...initialState,
} as Context);

export const AgencyNoticesProvider: React.FC = ({ children }) => {
  const { user } = useUser();

  const [state, setState] = useState(initialState);

  const closeAgencyNoticeHeaderBanner = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isAgencyNoticeHeaderBannerVisible: false,
    }));
  }, []);

  const loadInitialAgencyNotices = useCallback(async () => {
    if (!user) {
      return;
    }

    const clientToken = user.getClientToken();

    if (!clientToken) {
      return;
    }

    setState((prev) => ({
      ...prev,
      errorLoadingAgencyNotices: null,
      isLoadingAgencyNotices: true,
    }));

    const response = await agencyNoticesService.listClientAgencyNotices(
      clientToken,
    );

    if (response.isFailure()) {
      const error = response.data;

      setState((prev) => ({
        ...prev,
        errorLoadingAgencyNotices: error,
        isLoadingAgencyNotices: false,
      }));

      return;
    }

    const agencyNotices = response.data;

    setState((prev) => ({
      ...prev,
      agencyNotices,
      isLoadingAgencyNotices: false,
    }));
  }, [user]);

  const loadSubsequentAgencyNotices = useCallback(async () => {
    if (!user) {
      return;
    }

    const clientToken = user.getClientToken();

    if (!clientToken) {
      return;
    }

    setState((prev) => ({
      ...prev,
      errorLoadingAgencyNotices: null,
      isLoadingAgencyNotices: true,
    }));

    const response = await agencyNoticesService.listClientAgencyNotices(
      clientToken,
      AgencyNoticeExhibition.IMMEDIATE,
    );

    if (response.isFailure()) {
      const error = response.data;

      setState((prev) => ({
        ...prev,
        errorLoadingAgencyNotices: error,
        isLoadingAgencyNotices: false,
      }));

      return;
    }

    const incomingAgencyNotices = response.data;

    setState((prev) => {
      const existingAgencyNotices = prev.agencyNotices;

      const uniqueAgencyNotices = agencyNoticesHelper.mergeUniqueAgencyNotices(
        existingAgencyNotices,
        incomingAgencyNotices,
      );

      return {
        ...prev,
        agencyNotices: uniqueAgencyNotices,
        isLoadingAgencyNotices: false,
      };
    });
  }, [user]);

  useEffect(() => {
    void loadInitialAgencyNotices();
  }, [loadInitialAgencyNotices]);

  usePoll({
    // TODO: when exhibition config is ready, change to a function that takes it into account
    callback: loadSubsequentAgencyNotices,
    id: "load-subsequent-agency-notices",
    interval: isProdEnv()
      ? TEN_MINUTES_IN_MILLISECONDS
      : ONE_MINUTE_IN_MILLISECONDS,
    skipFirstPoll: true,
  });

  return (
    <AgencyNoticesContext.Provider
      value={{
        ...state,
        closeAgencyNoticeHeaderBanner,
      }}
    >
      {children}
    </AgencyNoticesContext.Provider>
  );
};

export const useAgencyNotices = useContextFactory(
  "AgencyNoticesContext",
  AgencyNoticesContext,
);
